import type { FC } from 'react';
import React from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon';
import { LazyEmojiComponentLoader } from '@confluence/emoji-title';
import {
	getEmojiDraftFromProperties,
	getEmojiPublishedFromProperties,
} from '@confluence/emoji-title/entry-points/transformer';
import {
	getEmojiFromTitle,
	removeEmojiFromTitle,
} from '@confluence/emoji-title/entry-points/helpers';
import { isValidEmojiOrFavicon } from '@confluence/emoji-title/entry-points/isValidEmojiOrFavicon';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiWrapper = styled.div<{ removeMargin?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: ({ removeMargin }) =>
		removeMargin ? token('space.0', '0px') : token('space.negative.075', '-6px'),
	width: '36px',
});

type ContentIconProps = {
	contentType: string;
	removeMargin: boolean;
	emoji?: string;
};

export const EmojiContentIcon: FC<ContentIconProps> = ({ contentType, removeMargin, emoji }) => {
	return isValidEmojiOrFavicon(emoji, contentType) ? (
		<EmojiWrapper data-testid="emoji" removeMargin={removeMargin}>
			<LazyEmojiComponentLoader
				emoji={emoji || ''}
				contentType={contentType}
				context="entityView"
				height={24}
			/>
		</EmojiWrapper>
	) : (
		<div data-testid="content-icon" style={{ marginRight: removeMargin ? '0' : '6px' }}>
			{/* @ts-expect-error FIXME: legacy ignore after migration; don't add new ones. Error: "label" must be specified for accessibility purposes */}
			<ContentIcon type={contentType} size="medium" />
		</div>
	);
};

export const getEmoji = (content) => {
	const emojiPropertyDraft = getEmojiDraftFromProperties(content);
	const emojiPropertyPublished = getEmojiPublishedFromProperties(content);
	const emojiProperty = content.status === 'draft' ? emojiPropertyDraft : emojiPropertyPublished;

	const emoji = emojiProperty || getEmojiFromTitle(content.title);

	return {
		hasEmojiProperty: Boolean(emojiProperty),
		emoji,
	};
};

export const getTitle = (originalTitle, hasEmojiProperty) =>
	hasEmojiProperty ? originalTitle : removeEmojiFromTitle(originalTitle);
